import { useEffect } from 'react';

import envConfig from '@app/enviroment/enviroment';
import * as Sentry from '@sentry/react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { isInjectedScriptError, REPLAY_UNMASK_SELECTORS } from './sentryFilters';

let SENTRY_DNS: string | boolean | number | undefined;
let SENTRY_ENVIRONMENT: string | undefined;

try {
  SENTRY_DNS = envConfig.getEnvKey('SENTRY_DNS');
  SENTRY_ENVIRONMENT = envConfig.getEnvKey('SENTRY_ENVIRONMENT') as string;
} catch (error) {
  console.warn('Sentry environment variables not configured:', error);
}

// Get the release version - this should match what's uploaded to Sentry
// Format: <package-version>-<environment>-<build-timestamp>
const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE || 'unknown';

console.log(`Sentry Release ${SENTRY_RELEASE} ENV: ${SENTRY_ENVIRONMENT}`);
if (SENTRY_DNS && typeof SENTRY_DNS === 'string') {
  Sentry.init({
    dsn: SENTRY_DNS as string,

    // Critical: This must match the release name used when uploading sourcemaps
    release: SENTRY_RELEASE,

    // Environment helps you filter issues by deployment (dev, staging, production, etc.)
    environment: SENTRY_ENVIRONMENT,

    integrations: [
      // See docs for support of different versions of variation of react router
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        // Mask all text and media by default for privacy
        maskAllText: true,
        blockAllMedia: true,
        unmask: REPLAY_UNMASK_SELECTORS,
      }),
    ],

    // Set tracesSampleRate based on environment
    // Lower in production to reduce quota usage
    tracesSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.1 : 1.0,

    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: [/^\//, /^https:\/\/.*\.konnecta\.io\/api/],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.05 : 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Filter out some common errors that aren't actionable
    beforeSend(event) {
      // Filter out browser extension errors
      if (event.exception?.values?.[0]?.value?.includes('chrome-extension://')) {
        return null;
      }

      // Filter out errors from browser extensions injecting scripts
      if (
        event.exception?.values?.[0]?.stacktrace?.frames?.some(
          (frame) => frame.filename?.includes('extension://') || frame.filename?.includes('moz-extension://')
        )
      ) {
        return null;
      }

      if (isInjectedScriptError(event)) {
        return null;
      }

      return event;
    },

    // Add additional context
    initialScope: {
      tags: {
        // Add any custom tags that help identify the deployment
        release_short: SENTRY_RELEASE.split('-')[0], // Just the version number
      },
    },
  });

  // Set user context if available (you can call this after authentication)
  // Sentry.setUser({ id: userId, email: userEmail });

  console.log(`Sentry initialized - Release: ${SENTRY_RELEASE}, Environment: ${SENTRY_ENVIRONMENT}`);
}

// Export utility functions for error reporting
export const captureError = (error: Error, context?: Record<string, unknown>) => {
  if (SENTRY_DNS) {
    Sentry.withScope((scope) => {
      if (context) {
        scope.setContext('additional_context', context);
      }
      Sentry.captureException(error);
    });
  }
  console.error(error);
};

export const captureMessage = (
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
  context?: Record<string, unknown>
) => {
  if (SENTRY_DNS) {
    Sentry.withScope((scope) => {
      if (context) {
        scope.setContext('additional_context', context);
      }
      Sentry.captureMessage(message, level);
    });
  }
  console.log(`[${level.toUpperCase()}]`, message);
};

export const setUserContext = (user: { id: string; email?: string; username?: string }) => {
  if (SENTRY_DNS) {
    Sentry.setUser(user);
  }
};

export const clearUserContext = () => {
  if (SENTRY_DNS) {
    Sentry.setUser(null);
  }
};
